import { refusalAt, type SchemaViolation } from 'src/shared/state/api-slices/engineErrorMessage';
import { IN_FORCE_UNTIL } from './stacItemDocument';
import type { AssetFormErrors, AssetFormValues, ItemFormErrors } from './stacItemDocument';
import type { CollectionFormErrors } from './stacCollectionDocument';

/**
 * Turning the engine's refusals into the errors a form already knows how to show.
 *
 * The engine points at a place in the *document*; a form has inputs. The two line up for most
 * fields and not for a few — a version's instant lives at `/properties/datetime`, its closure
 * under an extension key — so the correspondence is written out here rather than guessed at.
 *
 * Producing the form's own error shape is what makes this cheap: nothing downstream has to know
 * a refusal came from the engine rather than from validation, so every field that already shows
 * an error shows this one too, in the place it already shows it.
 */

/** Where in the document each of the dataset form's inputs lives. */
const COLLECTION_FIELDS: Record<string, string[]> = {
    id: ['id'],
    title: ['title'],
    description: ['description'],
    license: ['license'],
};

export const collectionRefusals = (violations: SchemaViolation[]): CollectionFormErrors =>
    Object.entries(COLLECTION_FIELDS).reduce<CollectionFormErrors>((errors, [field, path]) => {
        const refusal = refusalAt(violations, ...path);

        return refusal ? { ...errors, [field]: refusal } : errors;
    }, {});

/** Where in the document each of the version form's own inputs lives. */
export const ITEM_FIELDS: Record<string, string[]> = {
    id: ['id'],
    title: ['properties', 'title'],
    date: ['properties', 'datetime'],
    inForceUntil: ['properties', IN_FORCE_UNTIL],
};

/** The document's name for each of an asset row's inputs, where the two differ. */
const ASSET_FIELDS: Record<keyof AssetFormErrors, string> = {
    key: '',
    href: 'href',
    type: 'type',
    title: 'title',
    roles: 'roles',
    projCode: 'proj:code',
    extensionFields: '',
};

/**
 * An asset is addressed by the key it is published under, not by where it sits in the form, so
 * a refusal follows the asset even when the rows have been reordered.
 */
const assetRefusals = (violations: SchemaViolation[], assets: AssetFormValues[]): Record<number, AssetFormErrors> =>
    assets.reduce<Record<number, AssetFormErrors>>((byRow, asset, index) => {
        const refusals = Object.entries(ASSET_FIELDS).reduce<AssetFormErrors>((found, [formField, documentField]) => {
            if (!documentField || !asset.key) {
                return found;
            }

            const refusal = refusalAt(violations, 'assets', asset.key, documentField);
            return refusal ? { ...found, [formField]: refusal } : found;
        }, {});

        return Object.keys(refusals).length > 0 ? { ...byRow, [index]: refusals } : byRow;
    }, {});

export const itemRefusals = (violations: SchemaViolation[], assets: AssetFormValues[]): ItemFormErrors => ({
    fields: Object.entries(ITEM_FIELDS).reduce<ItemFormErrors['fields']>((errors, [field, path]) => {
        const refusal = refusalAt(violations, ...path);

        return refusal ? { ...errors, [field]: refusal } : errors;
    }, {}),
    assets: assetRefusals(violations, assets),
});

/**
 * The path, as one string, that a version form field corresponds to.
 *
 * For clearing a refusal when its field is edited: the form knows the field, and this says
 * which refusal was about it.
 */
export const DOCUMENT_PATHS: Record<string, string> = Object.fromEntries(
    Object.entries(ITEM_FIELDS).map(([field, path]) => [field, path.join('/')])
);

/**
 * What to show on each asset row: what the engine refused, and what this form found wrong.
 *
 * Validation wins where both speak about one input, for the same reason it does elsewhere — it
 * ran on what is on screen, the refusal on what was sent.
 */
export const mergeAssetErrors = (
    refusals: Record<number, AssetFormErrors>,
    found: Record<number, AssetFormErrors>
): Record<number, AssetFormErrors> => {
    const rows = new Set([...Object.keys(refusals), ...Object.keys(found)].map(Number));

    return Array.from(rows).reduce<Record<number, AssetFormErrors>>(
        (merged, row) => ({ ...merged, [row]: { ...refusals[row], ...found[row] } }),
        {}
    );
};
